import { SHIPPING_TYPES, SHIPPING_METHODS } from "./actionTypes";

export const selectShippingState = (state) => state.shipping;

export const selectIsLoading = (state) => state.shipping?.loading || false;

export const selectError = (state) => state.shipping?.error || null;

export const selectAddress = (state) => state.shipping?.address || {};

export const selectFullAddress = (state) => state.shipping?.address?.fullAddress || '';

export const selectAddressForCalculation = (state) => ({
    city: state.shipping?.address?.fullAddress || state.shipping?.address?.city || '',
    postal_code: state.shipping?.address?.postalCode || '',
});

export const selectIsAddressFilled = (state) => {
    const address = state.shipping?.address;
    return !!(address?.city || address?.fullAddress);
};

export const selectShippingOptions = (state) => state.shipping?.shippingOptions || {};

export const selectMinDelivery = (state) => state.shipping?.minDelivery || null;

export const selectMinPickup = (state) => state.shipping?.minPickup || null;

export const selectSelectedOption = (state) => state.shipping?.selectedOption || null;

export const selectSelectedShippingData = (state) => {
    const selectedOption = state.shipping?.selectedOption;
    const shippingOptions = state.shipping?.shippingOptions || {};

    if (!selectedOption || !shippingOptions[selectedOption]) {
        return null;
    }
    return shippingOptions[selectedOption];
};

export const selectSelectedShippingCost = (state) => {
    const pickupPoint = state.shipping?.selectedPickupPoint;
    if (pickupPoint && pickupPoint.amount > 0) {
        return pickupPoint.amount;
    }
    const data = selectSelectedShippingData(state);
    return data?.data?.shipping_details?.total_cost || 0;
};

export const selectSelectedDeliveryDays = (state) => {
    const data = selectSelectedShippingData(state);
    return data?.data?.shipping_details?.delivery_days || 0;
};

export const selectFormattedShippingOptions = (state) => {
    const shippingOptions = state.shipping?.shippingOptions || {};

    const methodNames = {
        [SHIPPING_METHODS.CDEK]: 'СДЭК',
        [SHIPPING_METHODS.OWN]: 'e-Feed',
        [SHIPPING_METHODS.DELLIN]: 'Деловые Линии',
    };

    const typeNames = {
        [SHIPPING_TYPES.DELIVERING]: 'Доставка по адресу',
        [SHIPPING_TYPES.LOCAL_PICKUP]: 'Пункт выдачи',
    };

    return Object.entries(shippingOptions).map(([key, option]) => {
        const { shipping_method, shipping_type, data } = option;
        const details = data?.shipping_details || {};

        const isOwnPickup = shipping_method === SHIPPING_METHODS.OWN
            && shipping_type === SHIPPING_TYPES.LOCAL_PICKUP;

        const methodName = isOwnPickup
            ? 'Самовывоз'
            : (methodNames[shipping_method] || shipping_method);
        const typeName = isOwnPickup ? '' : (typeNames[shipping_type] || shipping_type);
        const days = isOwnPickup ? 0 : (details.delivery_days || 0);
        const daysFormatted = days ? `за ${days} дн.` : '';

        return {
            key,
            method: shipping_method,
            type: shipping_type,
            methodName,
            typeName,
            cost: details.total_cost || 0,
            costFormatted: details.total_cost === 0
                ? 'бесплатно'
                : `${Math.round(details.total_cost || 0).toLocaleString()}₽`,
            days,
            daysFormatted,
            label: `${methodName}${typeName ? ` ${typeName}` : ''}${daysFormatted ? ` ${daysFormatted}` : ''}`,
        };
    });
};

export const selectShippingByType = (state) => {
    const formatted = selectFormattedShippingOptions(state);

    return {
        delivery: formatted.filter(opt => opt.type === SHIPPING_TYPES.DELIVERING),
        pickup: formatted.filter(opt => opt.type === SHIPPING_TYPES.LOCAL_PICKUP),
    };
};

export const selectPickupPoints = (state) => state.shipping?.pickupPoints || [];
export const selectPickupPointsLoading = (state) => state.shipping?.pickupPointsLoading || false;
export const selectPickupPointsError = (state) => state.shipping?.pickupPointsError || null;
export const selectSelectedPickupPoint = (state) => state.shipping?.selectedPickupPoint || null;

export const selectIsPickupSelected = (state) => {
    const selected = state.shipping?.selectedOption;
    if (!selected) return false;
    return selected.includes(SHIPPING_TYPES.LOCAL_PICKUP);
};

export const selectHasShippingOptions = (state) => {
    return Object.keys(state.shipping?.shippingOptions || {}).length > 0;
};

export const shippingSelectors = {
    selectShippingState,
    selectIsLoading,
    selectError,
    selectAddress,
    selectFullAddress,
    selectAddressForCalculation,
    selectIsAddressFilled,
    selectShippingOptions,
    selectMinDelivery,
    selectMinPickup,
    selectSelectedOption,
    selectSelectedShippingData,
    selectSelectedShippingCost,
    selectSelectedDeliveryDays,
    selectFormattedShippingOptions,
    selectShippingByType,
    selectHasShippingOptions,
    selectPickupPoints,
    selectPickupPointsLoading,
    selectPickupPointsError,
    selectSelectedPickupPoint,
    selectIsPickupSelected,
};

export default shippingSelectors;
